import React, { useEffect, useContext } from 'react';
import { graphql, useFragment } from 'react-relay';
import { Box, Flex, xcss } from '@atlaskit/primitives';
import Link from '@atlaskit/link';
import { fg } from '@atlassian/jira-feature-gating';
import { FieldsCompactModeContext } from '@atlassian/jira-issue-view-layout-templates-compact-mode-provider/src/index.tsx';
import { fireTrackAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type { detailsView_servicedeskCustomerServiceIssueViewPanel$key } from '@atlassian/jira-relay/src/__generated__/detailsView_servicedeskCustomerServiceIssueViewPanel.graphql';
import type { CustomerServiceCustomDetailsEntityType } from '@atlassian/jira-relay/src/__generated__/tableCustomDetailsQuery.graphql';
import { ThrowOnQueryError } from '@atlassian/jira-servicedesk-customer-service-common/src/ui/throw-on-query-error/index.tsx';
import { ReadOnlyCustomDetail } from '@atlassian/jira-servicedesk-customer-service-custom-details/src/ui/read-only-custom-detail/index.tsx';
import { useIsAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-admin/index.tsx';
import { type UFOExperience, ExperienceSuccess } from '@atlassian/ufo';
import { useIntl } from '@atlassian/jira-intl';
import { useIsAgentOrProjectAdmin } from '../../../utils.tsx';
import { EmptyState } from '../empty-state/index.tsx';
import messages from './messages.tsx';

type Props = {
	entityType: CustomerServiceCustomDetailsEntityType;
	details: detailsView_servicedeskCustomerServiceIssueViewPanel$key;
	editDetailsUrl: string;
	entityExperience?: UFOExperience;
	profileUrl?: string;
};

export const DetailsView = ({
	entityType,
	editDetailsUrl,
	details,
	entityExperience,
	profileUrl,
}: Props) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isJiraAdmin = useIsAdmin();
	const isAgentOrProjectAdmin = useIsAgentOrProjectAdmin();
	const isCompact = useContext(FieldsCompactModeContext);
	const { formatMessage } = useIntl();

	const data = useFragment(
		graphql`
			fragment detailsView_servicedeskCustomerServiceIssueViewPanel on CustomerServiceCustomDetailValuesQueryResult {
				__typename
				... on CustomerServiceCustomDetailValues {
					results {
						id
						...readOnlyCustomDetail_servicedeskCustomerServiceCustomDetails
					}
				}
				... on QueryError {
					...throwOnQueryError_servicedeskCustomerServiceCommon
				}
			}
		`,
		details,
	);

	useEffect(() => {
		if (data.__typename === 'CustomerServiceCustomDetailValues') {
			fireTrackAnalytics(createAnalyticsEvent({}), 'issueViewCustomerContextExpand loaded', {
				entityType,
				numberOfCsmDetails: data.results?.length || 0,
				isJiraAdmin,
				...(fg('jcs_detail_permissions') ? { isAgentOrProjectAdmin } : {}),
			});
		}
	}, [isAgentOrProjectAdmin, entityType, createAnalyticsEvent, isJiraAdmin, data]);

	if (data.__typename !== 'CustomerServiceCustomDetailValues') {
		return (
			<ThrowOnQueryError
				operationName="customDetails_servicedeskCustomerServiceIssueViewPanel"
				errorRef={data.__typename === 'QueryError' ? data : undefined}
			/>
		);
	}

	const { results } = data;

	if (results.length === 0) {
		return (
			<EmptyState
				context={entityType}
				detailsUrl={editDetailsUrl}
				entityExperience={entityExperience}
			/>
		);
	}

	return (
		<Box xcss={[wrapperStyles, isCompact && wrapperCompactStyles]}>
			{results.map((customDetailRef) => (
				<ReadOnlyCustomDetail
					key={customDetailRef.id}
					customDetailRef={customDetailRef}
					isInline={!isCompact}
				/>
			))}
			{profileUrl && fg('jcs_issue_layout_eap') && (
				<Flex wrap="wrap">
					<Box xcss={linkSpaceStyles} />
					<Box xcss={linkStyles}>
						<Link href={profileUrl}>{formatMessage(messages.viewProfile)}</Link>
					</Box>
				</Flex>
			)}
			{entityExperience && <ExperienceSuccess experience={entityExperience} />}
		</Box>
	);
};

const wrapperStyles = xcss({
	display: 'flex',
	flexDirection: 'column',
	gap: 'space.050',
});

const wrapperCompactStyles = xcss({
	gap: 'space.100',
});

// Hardcoded values to match those in
// src/packages/servicedesk/customer-service/custom-details/src/common/ui/layout/default/index.tsx
// So that the link is vertically aligned with the detail values

const linkSpaceStyles = xcss({
	width: '37%',
	minWidth: '90pt',
	paddingInlineEnd: 'space.150',
	flexGrow: 1,
	maxWidth: '170px',
});

const linkStyles = xcss({
	flexGrow: 1,
	width: 'calc(60% + 16px)',
	padding: 'space.100',
	marginInline: 'space.negative.100',
});
